import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getPromptWithVariables } from '../../../helpers';
import { ClaudeActionCreators } from '../../../redux/actions/creators';
import { AsyncActionStatus } from '../../../redux/enums';
import useTooltip from '../../../hooks/useTooltip';
import ArrowIcon from '../../../../assets/arrow-send.svg';
import ToggleSwitchButton from '../ToggleSwitchButton';
import TextArea from '../TextArea';
import DropdownV2 from '../DropdownV2';
import { GenerationType } from '../../../../../shared/enums';

import styles from '../../../styles/views/components/AIChatbot/Chat.module.scss';

const Chat = ({
  promptOptions,
  onSend,
  isAnswerLoading,
  isTranscriptIncluded,
  handleTranscriptIncludedToggle,
  chapterData,
  nextChapterData,
  tokensMultiplier,
  generationType,
}) => {
  useTooltip();
  const dispatch = useDispatch();

  const { projectId } = useParams();
  const ref = useRef(null);
  const [message, setMessage] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState('');

  useEffect(() => {
    if (selectedPromptId) {
      const selectedOption = promptOptions.find((option) => option._id === selectedPromptId);
      let newMessage = selectedOption?.message;
      if (generationType === GenerationType.DRAFT) {
        newMessage = getPromptWithVariables(chapterData, nextChapterData, selectedOption?.message);
      }
      setMessage(newMessage);
    }
  }, [selectedPromptId, promptOptions, chapterData, nextChapterData, generationType]);

  const handleSendMessage = useCallback(() => {
    dispatch(
      ClaudeActionCreators.askClaude[AsyncActionStatus.PENDING](
        message,
        projectId,
        isTranscriptIncluded,
        tokensMultiplier,
      ),
    );
    onSend(message);
    setMessage('');
    setSelectedPromptId('');
  }, [onSend, message, dispatch, projectId, isTranscriptIncluded, tokensMultiplier]);

  const handleChangeMessage = useCallback((e) => setMessage(e.target.value), []);

  const handleChangePrompt = useCallback((prompt) => setSelectedPromptId(prompt.value), []);


  return (
    <div className={styles.container}>
      <DropdownV2
        isSearchable
        name="prompt"
        label={t('components.prompt.label', { ns: i18next.Namespace.COMMON })}
        placeholder={t('components.prompt.placeholder', { ns: i18next.Namespace.COMMON })}
        primitiveValue={selectedPromptId}
        options={promptOptions}
        optionValue="_id"
        optionLabel="name"
        onChange={handleChangePrompt}
        componentsStyles={{
          menuList: { maxHeight: '104px' },
          option: { fontSize: '16px !important', padding: '4px 7px' },
          singleValue: { fontSize: '16px !important', paddingTop: '3px' },
          placeholder: { fontSize: '16px !important', paddingTop: '3px' },
        }}
      />
      <div className={styles.messageWrapper}>
        <TextArea
          autoGrow
          hideCounter
          ref={ref}
          value={message}
          name="message"
          placeholder={t('components.inputPlaceholder', { ns: i18next.Namespace.COMMON })}
          defaultHeight="24px"
          maxHeight={200}
          className={styles.messageInput}
          onChange={handleChangeMessage}
        />
        <ArrowIcon
          className={`${styles.arrowIcon} ${(!isAnswerLoading && message) ? styles.activeIcon : ''}`}
          onClick={handleSendMessage}
          data-tip={(isAnswerLoading && message) ? t('components.gennyIsThinking', { ns: i18next.Namespace.COMMON }) : ''}
        />
        <div
          className={styles.toggleWrapper}
          data-tip={isTranscriptIncluded ? t('components.transcriptsIncluded', { ns: i18next.Namespace.COMMON }) : t('components.transcriptsExcluded', { ns: i18next.Namespace.COMMON })}
        >
          <span>{t('components.includeTranscripts', { ns: i18next.Namespace.COMMON })}</span>
          <ToggleSwitchButton
            customeWrapperStyles={styles.toggleSwitch}
            isChecked={isTranscriptIncluded}
            handleChange={handleTranscriptIncludedToggle}
          />
        </div>
      </div>
    </div>
  );
};

Chat.propTypes = {
  generationType: PropTypes.string.isRequired,
  chapterData: PropTypes.object,
  nextChapterData: PropTypes.object,
  promptOptions: PropTypes.array,
  onSend: PropTypes.func,
  handleTranscriptIncludedToggle: PropTypes.func,
  isAnswerLoading: PropTypes.bool,
  isTranscriptIncluded: PropTypes.bool,
  tokensMultiplier: PropTypes.number,
};

Chat.defaultProps = {
  chapterData: {},
  nextChapterData: {},
  promptOptions: [],
  onSend: () => {},
  handleTranscriptIncludedToggle: () => {},
  isAnswerLoading: false,
  isTranscriptIncluded: true,
  tokensMultiplier: 1,
};

export default Chat;
